import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CustomButton from "../../../components/customButton/customButton";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import Loader from "../../../components/loader";
import GridStyles from "../../../assets/css/grid.styles";
import CustomDataTable from "../../../components/customDataTable/customDataTable";
import { CustomTableColumnsProps } from "../../../components/customDataTable/customTableColumnsProps";
import CustomIcon from "../../../components/customIcon/customIcon";
import { PermissionsActions } from "../../../constants/permissionsActions";
import { PermissionsActionsModel } from "../../../models/models/permissionsActionsModel";
import { UserPermissionsModel } from "../../../models/models/userPermissionsModel";
import { getInvestorList } from "../../../reducers/investorReducer";

const InvestorListForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const investorListState = useSelector(
    (state: RootState) => state.investor.investorList
  );
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataReloadFlag, setDataReloadFlag] = useState(false);
  const [key, setKey] = useState<number>(0);
  const [sortType, setSortType] = useState<"asc" | "desc">("asc");
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);

  const userPermissions: UserPermissionsModel[] = JSON.parse(
    localStorage.getItem("userPermissions") ?? "{}"
  );

  for (let i = 0; i < userPermissions.length; i++) {
    const { permission, pageUrl, actions } = userPermissions[i];
    if (permission === "All" || pageUrl === "/Investors") {
      actions.forEach((action) => {
        PermissionsActions[
          action.toLowerCase() as keyof PermissionsActionsModel
        ] = true;
      });
      break;
    }
  }

  const columns: CustomTableColumnsProps[] = [
    {
      id: "firstName",
      label: "First Name",
      width: "100",
      align: "left",
      visible: true,
    },
    {
      id: "lastName",
      label: "Last Name",
      width: "100",
      align: "left",
      visible: true,
    },
    {
      id: "email",
      label: "Email",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "mobileNumber",
      label: "Mobile",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "country",
      label: "Country",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "city",
      label: "City",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "birthDate",
      label: "Birth Date",
      width: "150",
      align: "left",
      visible: true,
    },
    {
      id: "actions",
      visible: true,
      label: "Actions",
      width: "150",
      align: "center",
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          {PermissionsActions.edit && (
            <CustomIcon
              tooltipText="Edit"
              onClick={() => handleEditClick(params.row)}
              style={GridStyles.editIcon}
            >
              <EditIcon />
            </CustomIcon>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!authToken) {
      navigate("/Signin");
    }
  }, [authToken]);

  useEffect(() => {
    const filterData = {
      pageSize,
      currentPage,
      sortType,
      sortBy
    };
    dispatch(getInvestorList({ data: filterData })).then((val: any) => {
      if (val.payload) {
        const updatedData = val.payload.item2.map((item: any) => ({
          ...item,
          birthDate: item.birthDate?.split(" ")[0] || "",
        }));
        setData(updatedData);
      }
    });
  }, [dispatch, pageSize, currentPage, dataReloadFlag, sortType, sortBy]);

  const handleEditClick = (row: any) => {
    const editId = row ? row.id : 0;
    navigate(`/Registration/${editId}`);
  };

  const handleAddClick = () => {
    navigate(`/investor`);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleSortChange = (columnId: string) => {
    setSortType(sortType === "asc" ? "desc" : "asc");
    setSortBy(columnId);
  };

  return (
    <div style={GridStyles.container}>
      <div style={GridStyles.header}>
        <div style={GridStyles.titleContainer}>
          <h1 style={GridStyles.title}>Investors List</h1>
        </div>
        <div style={GridStyles.addButtonContainer}>
          {PermissionsActions.add && (
            <CustomButton
              type="button"
              style={GridStyles.addButton}
              onClick={() => handleAddClick()}
            >
              <AddIcon style={GridStyles.iconStyle} /> New Investor
            </CustomButton>
          )}
        </div>
      </div>

      {investorListState.isLoading && <Loader />}
      <CustomDataTable
        key={key}
        columns={columns}
        count={
          investorListState.data &&
          typeof investorListState.data === "object" &&
          "item1" in investorListState.data
            ? investorListState.data.item1
            : 0
        }
        data={
          investorListState.data &&
          typeof investorListState.data === "object" &&
          "item2" in investorListState.data
            ? data
            : []
        }
        isColored={true}
        sortBy={sortBy}
        sortType={sortType}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default InvestorListForm;
